/**
 * Calculate the effective cleaning time applying the type modifier.
 * 
 * @param {number} baseTimeMinutes - Base cleaning time from the room (in minutes)
 * @param {string} modifierType - 'none', 'relative', 'percentage', or 'absolute'
 * @param {number} modifierValue - The modifier value
 * @returns {number} The calculated cleaning time in minutes (minimum 1 minute)
 */
export function calculateModifiedCleaningTime(baseTimeMinutes, modifierType = 'none', modifierValue = 0) {
  if (!baseTimeMinutes || baseTimeMinutes <= 0) {
    baseTimeMinutes = 30; // Default
  }

  if (modifierType === 'none' || !modifierType) {
    return baseTimeMinutes;
  }

  if (modifierType === 'absolute') {
    // Absolute: use the modifier value directly, ignore base time
    return Math.max(1, modifierValue);
  }

  if (modifierType === 'relative') {
    // Relative: add/subtract minutes from base time
    return Math.max(1, baseTimeMinutes + modifierValue);
  }

  if (modifierType === 'percentage') {
    // Percentage: add/subtract percentage of base time
    const adjustment = (baseTimeMinutes * modifierValue) / 100;
    return Math.max(1, Math.round(baseTimeMinutes + adjustment));
  }

  // Default fallback
  return baseTimeMinutes;
}

/**
 * Format cleaning time for display
 * @param {number} minutes - Time in minutes
 * @returns {string} Formatted string like "30 min" or "1h 30min"
 */
export function formatCleaningTime(minutes) {
  if (!minutes || minutes <= 0) return '-';
  
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (mins === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${mins}min`;
}

/**
 * Get modifier description for display
 * @param {string} type - Modifier type
 * @param {number} value - Modifier value
 * @returns {string} Human readable description
 */
export function getModifierDescription(type, value) {
  if (type === 'none' || !type) {
    return 'Sin modificación';
  }
  
  if (type === 'absolute') {
    return `Fijo: ${value} min`;
  }
  
  if (type === 'relative') {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value} min`;
  }
  
  if (type === 'percentage') {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value}%`;
  }
  
  return '-';
}
